import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import * as JsBarcode from 'jsbarcode';

@Injectable({
  providedIn: 'root'
})
export class PrintService {

  /**
   * Déclarations @font-face des polices Casto, injectées dans les fenêtres d'impression
   * qui ne bénéficient pas des styles de l'application.
   */
  private static readonly CASTO_FONT_FACES = `
      @font-face {
        font-family: 'Casto Pro Regular';
        src: local('Casto Pro Regular'), local('Casto-Pro-Regular'),
             url('assets/fonts/CastoPro-Regular/CastoPro-Regular.woff2') format('woff2'),
             url('assets/fonts/CastoPro-Regular/CastoPro-Regular.woff') format('woff'),
             url('assets/fonts/CastoPro-Regular/CastoPro-Regular.ttf') format('truetype');
        font-weight: 400;
        font-style: normal;
      }
      @font-face {
        font-family: 'Casto Pro Bold';
        src: local('Casto Pro Bold'), local('Casto-Pro-Bold'),
             url('assets/fonts/CastoPro-Bold/CastoPro-Bold.woff2') format('woff2'),
             url('assets/fonts/CastoPro-Bold/CastoPro-Bold.woff') format('woff'),
             url('assets/fonts/CastoPro-Bold/CastoPro-Bold.ttf') format('truetype');
        font-weight: 700;
        font-style: normal;
      }`;

  printContainer: HTMLDivElement;

  constructor(private titleService: Title) {
    this.printContainer = document.createElement('div');
    this.printContainer.className = 'print-container container-fluid';
    window.document.body.appendChild(this.printContainer);
  }

  hasContent(): boolean {
    return this.printContainer.innerHTML != '';
  }

  reset() {
    this.printContainer.innerHTML = '';
  }

  addImage(src: string): Promise<void> {
    let _i = new Image();
    _i.className = 'img-fluid';
    this.printContainer.appendChild(_i);

    return new Promise((resolve, reject) => {
      _i.onload = (e: any) => {
        resolve();
      };
      _i.src = src;
    });

  }

  addText(text: string, style: string) {
    let _p = document.createElement('p');
    _p.innerHTML = text;
    _p.className = style || '';
    this.printContainer.appendChild(_p);
  }

  addDiv(div: HTMLDivElement): Promise<void> {
    this.printContainer.appendChild(div);
    return new Promise((resolve, reject) => {
      let _imgs = div.querySelectorAll("img");

      if (_imgs.length > 0) {

        let _imgToLoadCount = _imgs.length;
        let _imgLoaded = 0;

        for (let i = 0; i < _imgToLoadCount; i++) {
          const _img: HTMLImageElement = _imgs[i];

          if (!_img.complete) {
            _img.onload = (e: any) => {
              _imgLoaded++;
              if (_imgLoaded === _imgToLoadCount) resolve();
            };
            _img.onerror = (e: any) => {
              _imgLoaded++;
              if (_imgLoaded === _imgToLoadCount) resolve();
            };
          } else {
            _imgLoaded++;
            if (_imgLoaded === _imgToLoadCount) resolve();
          }
        }
      } else {
        resolve();
      }
    });
  }

  addTitleRow(level: number, text: string) {
    let _t = document.createElement('h' + level);
    _t.innerHTML = text;
    _t.className = 'bg-blue';
    this.printContainer.appendChild(_t);
  }

  addDom(dom: HTMLElement): void {
    const clone = new DOMParser()
      .parseFromString(dom.innerHTML, 'text/html')
      .children
      .item(0)
      .getElementsByTagName('body')
      .item(0)
      .firstChild;
    this.printContainer.appendChild(clone);
  }

  print(specificName: string = undefined) {
    let oldTitle = this.titleService.getTitle();
    if (specificName != undefined) {
      this.titleService.setTitle(specificName);
    }
    window.print();
    if (specificName != undefined) {
      this.titleService.setTitle(oldTitle);
    }
  }

  /**
   * Ouvre une fenêtre d'impression contenant le code barre, son titre et,
   * si elle est fournie, sa date de validité.
   * @param title titre affiché au-dessus du code barre (ex : "Carte C-Casto Max")
   * @param value valeur encodée dans le code barre
   * @param img image optionnelle affichée sous le titre
   * @param subtitle mention optionnelle affichée sous le titre (ex : "Valable jusqu'au 31/12/2026")
   */
  printBarcode(title: string, value: number | string, img?: string, subtitle?: string) {
    return new Promise(() => {
      setTimeout(() => {
          let popupWin = window.open("", "_blank", "top=0,left=0,height=0,width=auto");
          popupWin.document.open();
          popupWin.document.write(`
                  <html>
                    <head>
                    <meta charset="utf-8">
                    <!-- Permet à la popup (about:blank) de résoudre les URLs relatives des polices -->
                    <base href="${document.baseURI}">
                    <style>
                    ${PrintService.CASTO_FONT_FACES}
                    body {
                      margin: 0;
                      background-color: #fff;
                      font-family: 'Casto Pro Regular', Arial, sans-serif;
                      color: #000;
                    }
                    .print-container {
                      display: block;
                      text-align: center;
                      background-color: white;
                      border: 1px solid #EBF6FF;
                      border-radius: 16px;
                      padding: 1.5em;
                    }
                    .print-title {
                      font-family: 'Casto Pro Bold', Arial, sans-serif;
                      font-size: 26px;
                      line-height: 1.2;
                      color: #0078d4;
                      margin: 0;
                    }
                    .print-subtitle {
                      font-family: 'Casto Pro Regular', Arial, sans-serif;
                      font-size: 16px;
                      line-height: 1.4;
                      color: #58626D;
                      margin: 0.6em 0 0;
                    }
                    .print-barcode {
                      margin-top: 1.5em;
                    }
                    .print-barcode svg {
                      max-width: 100%;
                      height: auto;
                    }
                    .img-fluid {
                      max-width: 100%;
                      height: auto;
                      margin-top: 1em;
                    }
                    .no-print {
                      text-align: center;
                      align-items: center;
                      width: 100vw;
                      height:100vh;
                    }
                    .heading-print {
                      font-family: 'Casto Pro Bold', Arial, sans-serif;
                      background-color:#e6e6e6;
                      font-size: 30px;
                      font-weight: bold;
                      color: #20407f;
                    }
                    .display-tbl {
                      border-collapse: collapse;
                      table-layout: fixed;
                      width: 100%;
                      height:100%;
                    }
                    .display-tbl td {
                      text-align: center;
                      width: 100%;
                    }
                   .print{
                      display: none;
                    }
                    @media print {
                        @page {
                          margin: 1cm;
                        }
                        .print {
                          display: block;
                        }
                        .no-print {
                          display: none;
                        }
                    }
                  </style>
                    </head>
                    <body>
                      <div class="no-print heading-print">
                        <table class="display-tbl">
                        <tr>
                           <td><h1 style="text-align: center; font-size: 30px; font-weight: bold;">Cliquer/Toucher pour fermer</h1></td>
                        </tr>
                        </table>
                      </div>
                      <div class="print print-container">
                        <h1 class="print-title">${title}</h1>
                        ${subtitle ? `<p class="print-subtitle">${subtitle}</p>` : ''}
                        ${img ? `<img class="img-fluid" src="${img}">` : ''}
                        <div class="print-barcode">
                          ${this.createBarCode(value)}
                        </div>
                      </div>
                      <script defer>
                        function triggerPrint(event) {
                          window.removeEventListener('load', triggerPrint, false);
                          // On attend le chargement des polices Casto pour que le rendu imprimé soit conforme
                          var fontsReady = document.fonts ? document.fonts.ready : Promise.resolve();
                          fontsReady.then(function() {
                            setTimeout(function() {
                              closeWindow(window.print());
                            }, ${1000});
                          });
                        }
                        function closeWindow(){}
                        function winclose(){
                          window.close();
                        };
                        window.document.body.addEventListener('click', winclose, true);
                        window.document.body.addEventListener('touchstart', winclose, true);
                        window.document.body.addEventListener('touchmove', winclose, true);
                        window.document.body.addEventListener('touchcancel', winclose, true);
                        window.addEventListener('load', triggerPrint, false);
                      </script>
                    </body>
                  </html>`);
          popupWin.document.close();
        }
      , 0);
    });
  }

  createBarCode(value: number | string): string {
    const xmlSerializer = new XMLSerializer();
    const svgNode = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    
    JsBarcode(svgNode, `${value}`, {
        xmlDocument: document,
        height: 50,
        width: 2.3,
        text: '' + value,
        font: "'Casto Pro Bold', Arial, sans-serif",
        fontOptions: 'bold',
        fontSize: 25,
        lineColor: '#000000',
        margin: 20,
        textMargin: 5,
        textAlign: 'center',
        textPosition: 'top'
    });
    
    return xmlSerializer.serializeToString(svgNode);
  }
}
